import { Controller } from '@hotwired/stimulus'
import { createFocusTrap, type FocusTrap, type Options } from 'focus-trap'

export default class FocusTrapController extends Controller<HTMLElement> {
  declare trap?: FocusTrap
  declare whitelistValue: Array<String>

  static values = {
    whitelist: { type: Array<String>, default: [] },
  }

  // Stimulus lifecycle methods

  connect() {
    if (!this.#hasFocusableNodes()) return
    this.trap = createFocusTrap(this.element, this.#getOptions())
  }

  disconnect() {
    this.trap?.deactivate()
  }

  // `focus-trap` instance methods

  activate() {
    this.trap?.activate()
  }

  deactivate() {
    if (!this.trap?.active) return
    this.trap?.deactivate()
  }

  pause() {
    this.trap?.pause()
  }

  toggle() {
    if (!this.trap) return

    this.trap.active ? this.deactivate() : this.activate()
  }

  unpause() {
    this.trap?.unpause()
  }

  // Private methods

  // Dispatch events manually, since `focus-trap` doesn't dispatch events itself
  #eventOptions() {
    return {
      onActivate: () => this.dispatch('activate'),
      onPostActivate: () => this.dispatch('post-activate'),
      onPause: () => this.dispatch('pause'),
      onPostPause: () => this.dispatch('post-pause'),
      onUnpause: () => this.dispatch('unpause'),
      onPostUnpause: () => this.dispatch('post-unpause'),
      onDeactivate: () => this.dispatch('deactivate'),
      onPostDeactivate: () => this.dispatch('post-deactivate'),
    }
  }

  #getOptions() {
    const { focusTrapOptions } = this.element.dataset
    const options: Options = focusTrapOptions ? JSON.parse(focusTrapOptions) : {}

    // Allow clicks to whitelisted elements
    options.allowOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement
      for (const selector of this.whitelistValue) {
        if (target.closest(selector as string) !== null) {
          return true
        }
      }

      return false
    }

    return { ...this.#eventOptions(), ...options }
  }

  #hasFocusableNodes() {
    const focusables = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ]

    return this.element.querySelectorAll(focusables.join(', ')).length > 0
  }
}
